/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { Component, Fragment } from 'react';
import propTypes from 'prop-types';
import classnames from 'classnames';
import get from 'lodash/get';
import range from 'lodash/range';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { Collapse, Input, Label } from 'reactstrap';

import { utcToPtDateString } from 'lib/date';
import { IconButton, SectionSubHeader } from 'ui/Components';
import { addSection, trash as deleteSection } from 'ui/Icons';
import { renderArrowStyle } from 'ui/helpers';

import NodeInfo from './NodeInfo';
import SeoSettings from './SeoSettings';
import styles from './PublishOptions.css';

/**
 * Render routing options for page
 */
export default class PublishOptions extends Component {
  static propTypes = {
    categoryId: propTypes.string.isRequired,
    changePagePropertyDetail: propTypes.func.isRequired,
    headline: propTypes.string.isRequired,
    isCompassPage: propTypes.bool,
    isReadonly: propTypes.bool,
    node: propTypes.array.isRequired,
    pagePayload: propTypes.object.isRequired,
    routeMapping: propTypes.array.isRequired,
    seoSettings: propTypes.object.isRequired
  };

  static defaultProps = {
    isCompassPage: false,
    isReadonly: true
  };

  constructor(props) {
    super(props);

    this.state = {
      seoSettings: !isEmpty(props.seoSettings)
        ? props.seoSettings
        : {
            PageTitle: '',
            PageDescription: '',
            CanonicalUrl: '',
            isNoIndex: false
          },
      routeMapping: props.routeMapping || [],
      isCompassPage: Boolean(props.isCompassPage),
      categoryId: props.categoryId || '',
      headline: props.headline || '',
      showSeoSettings: true,
      showNodeInfo: true
    };

    this.renderPublishInfo = this.renderPublishInfo.bind(this);
    this.increaseRouteCount = this.increaseRouteCount.bind(this);
    this.decreaseRouteCount = this.decreaseRouteCount.bind(this);
    this.handleRouteChange = this.handleRouteChange.bind(this);
    this.handleRouteUpdate = this.handleRouteUpdate.bind(this);
    this.handleRemoveContentToggle = this.handleRemoveContentToggle.bind(this);
    this.handleCategoryIdInputChange = this.handleCategoryIdInputChange.bind(this);
    this.handleCategoryIdUpdate = this.handleCategoryIdUpdate.bind(this);
    this.handleSeoFieldChange = this.handleSeoFieldChange.bind(this);
    this.handleSeoFieldUpdate = this.handleSeoFieldUpdate.bind(this);
    this.handleSeoCheckedChange = this.handleSeoCheckedChange.bind(this);
    this.handleHeadlineInputChange = this.handleHeadlineInputChange.bind(this);
    this.handleHeadlineUpdate = this.handleHeadlineUpdate.bind(this);
    this.toggleSeoSettings = this.toggleSeoSettings.bind(this);
    this.toggleNodeSettings = this.toggleNodeSettings.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if (!isEqual(this.props, nextProps)) {
      this.setState({
        seoSettings: {
          PageTitle: nextProps.seoSettings.PageTitle || '',
          PageDescription: nextProps.seoSettings.PageDescription || '',
          CanonicalUrl: nextProps.seoSettings.CanonicalUrl || '',
          isNoIndex: nextProps.seoSettings.isNoIndex || ''
        },
        routeMapping: nextProps.routeMapping || [],
        isCompassPage: nextProps.isCompassPage,
        categoryId: nextProps.categoryId || ''
      });
    }
  }

  /**
   * Handle route value change
   * @param {object} e - event
   */
  handleRouteChange(e) {
    const { value } = e.target;
    const { key } = e.target.dataset;
    const { routeMapping: newRouteMapping } = this.state;

    newRouteMapping[key] = {
      ...newRouteMapping[key],
      RouteURL: value
    };

    if (typeof get(newRouteMapping[key], 'IsEnabled') === 'undefined' && value.length) {
      newRouteMapping[key] = {
        ...newRouteMapping[key],
        IsEnabled: 'Yes'
      };
    }

    this.setState({ routeMapping: newRouteMapping });
  }

  /**
   * Handle route input on blur
   */
  handleRouteUpdate() {
    const { changePagePropertyDetail } = this.props;
    const { routeMapping } = this.state;

    const mapping = routeMapping.every(route => route.RouteURL === '') ? [] : routeMapping;

    changePagePropertyDetail(mapping, 'Payload.MetadataFields.Route.RouteMapping');
  }

  /**
   * Handle publish/unpublish option based on selection
   * @param {object} e - event
   */
  handleRemoveContentToggle(e) {
    const { key } = e.target.dataset;
    const { changePagePropertyDetail } = this.props;
    const { routeMapping } = this.state;
    const newRouteMapping = routeMapping;

    newRouteMapping[key] = {
      ...newRouteMapping[key],
      IsEnabled: e.target.checked ? 'No' : 'Yes'
    };

    changePagePropertyDetail(newRouteMapping, 'Payload.MetadataFields.Route.RouteMapping');
  }

  /**
   * Handle category ID change
   * @param {object} e - event
   */
  handleCategoryIdInputChange(e) {
    this.setState({ categoryId: e.target.value });
  }

  /**
   * Handle category ID update
   */
  handleCategoryIdUpdate() {
    const { changePagePropertyDetail } = this.props;
    const { categoryId } = this.state;
    changePagePropertyDetail(categoryId, 'Payload.MetadataFields.CategoryId');
  }

  /**
   * handle headline title change
   * @param {object} e - change event
   */
  handleHeadlineInputChange(e) {
    this.setState({ headline: e.target.value });
  }

  /**
   * handle headline title update
   */
  handleHeadlineUpdate() {
    const { changePagePropertyDetail } = this.props;
    const { headline } = this.state;
    changePagePropertyDetail(headline, 'Payload.MetadataFields.Headline');
  }

  /**
   * handle seo text change
   * @param {object} e - event
   */
  handleSeoFieldChange(e) {
    const { value, name } = e.target;
    const { seoSettings } = this.state;

    const newSeoSettings = {
      ...seoSettings,
      [name]: value
    };

    this.setState({ seoSettings: newSeoSettings });
  }

  /**
   * handle seo text on blur
   */
  handleSeoFieldUpdate() {
    const { changePagePropertyDetail, seoSettings: originalSeoSettings } = this.props;
    const { seoSettings } = this.state;

    if (!isEqual(originalSeoSettings, seoSettings)) {
      changePagePropertyDetail(seoSettings, 'Payload.MetadataFields.SeoSettings');
    }
  }

  /**
   * handle seo checkbox change
   * @param {object} e - event
   */
  handleSeoCheckedChange(e) {
    const { changePagePropertyDetail } = this.props;
    const { name, checked } = e.target;
    const { seoSettings } = this.state;
    const newSeoSettings = {
      ...seoSettings,
      [name]: checked
    };
    if (!isEqual(seoSettings, newSeoSettings)) {
      changePagePropertyDetail(newSeoSettings, 'Payload.MetadataFields.SeoSettings');
    }
    this.setState({ seoSettings: newSeoSettings });
  }

  /**
   * Setup list of routes
   * @param {boolean} publish - is published or scheduled (defaults to false)
   * @returns {Component} list of routes
   */
  setupRoutes(publish = false) {
    const { isReadonly } = this.props;
    const { categoryId, headline, routeMapping } = this.state;

    let routeCount;
    let newRouteMapping;

    if (routeMapping.length) {
      routeCount = routeMapping.length;
      newRouteMapping = routeMapping.map(route => ({
        ...route,
        IsPublished: route.IsPublished !== false && publish
      }));
    } else {
      routeCount = 1;
      newRouteMapping = [{ IsEnabled: 'Yes', RouteURL: '', IsPublished: false }];
    }

    // disable the remove content option when there is only one route
    const isRemoveContentDisabled = routeCount <= 1;

    const renderRouteList = range(routeCount).map(key => (
      <div className={classnames(styles.route, 'p-2')} key={key}>
        {routeCount > 1 && (
          <div
            className={styles.removeRouteItem}
            onClick={this.decreaseRouteCount}
            data-key={key}
            data-test-id={`publish-options-route-toggle-decrease-${key}`}
          >
            <IconButton
              alt="remove route"
              className={styles.removeImage}
              hidden={isReadonly}
              icon={deleteSection}
              rem={1.5}
            />
          </div>
        )}
        <Label className="font-weight-bold" for={`route-${key}`}>
          Route
        </Label>
        <Input
          name={`route-${key}`}
          type="text"
          data-key={key}
          value={newRouteMapping[key].RouteURL}
          onChange={this.handleRouteChange}
          onBlur={this.handleRouteUpdate}
          disabled={
            isReadonly ||
            newRouteMapping[key].IsEnabled === 'No' ||
            newRouteMapping[key].IsPublished === true
          }
          data-test-id={`publish-options-route-text-input-${key}`}
        />
        <div
          className={classnames({ [styles.disableRemoveContent]: isRemoveContentDisabled })}
        >
          <Input
            name={`route-${key}`}
            type="checkbox"
            data-key={key}
            disabled={isReadonly || isRemoveContentDisabled}
            onChange={this.handleRemoveContentToggle}
            checked={newRouteMapping[key].IsEnabled === 'No'}
            data-test-id={`publish-options-route-toggle-${key}`}
          />
          Remove Content
        </div>
      </div>
    ));

    const renderCategoryId = (
      <div className="p-2">
        <Label className="font-weight-bold" for="categoryId">
          Category ID
        </Label>
        <Input
          disabled={isReadonly}
          name="categoryId"
          type="text"
          value={categoryId}
          onChange={this.handleCategoryIdInputChange}
          onBlur={this.handleCategoryIdUpdate}
          data-test-id="publish-options-category-id-input"
        />
      </div>
    );

    const renderHeadline = (
      <div className="p-2">
        <Label className="font-weight-bold" for="headline">
          H1 Title
        </Label>
        <Input
          disabled={isReadonly}
          name="headline"
          type="text"
          value={headline}
          onChange={this.handleHeadlineInputChange}
          onBlur={this.handleHeadlineUpdate}
          data-test-id="publish-options-headline-title-input"
        />
      </div>
    );

    return (
      <Fragment>
        {renderRouteList}
        {renderCategoryId}
        {renderHeadline}
      </Fragment>
    );
  }

  /**
   * Increase number of routes displayed
   */
  increaseRouteCount() {
    const { routeMapping } = this.state;
    this.setState({
      routeMapping: [...routeMapping, { IsEnabled: 'Yes', RouteURL: '', IsPublished: false }]
    });
  }

  /**
   * Decrease number of routes displayed
   * @param {object} e - event
   */
  decreaseRouteCount(e) {
    const { changePagePropertyDetail } = this.props;
    const { routeMapping } = this.state;
    const newRouteMapping = routeMapping;
    const { key } = e.currentTarget.dataset;

    newRouteMapping.splice(key, 1);

    changePagePropertyDetail(newRouteMapping, 'Payload.MetadataFields.Route.RouteMapping');
    this.setState({ routeMapping: newRouteMapping });
  }

  /**
   * Increase the number of route inputs
   * @returns {Component} increase button
   */
  showRouteToggleButtons() {
    const { isReadonly } = this.props;
    return (
      <div className={classnames(styles.routeToggles, 'px-4')}>
        <div
          className={styles.increaseRouteCount}
          onClick={this.increaseRouteCount}
          data-test-id="publish-options-route-toggle-increase"
        >
          <IconButton
            alt="add an additional route"
            hidden={isReadonly}
            icon={addSection}
            rem={1.5}
          />
        </div>
      </div>
    );
  }

  /**
   * toggles whether seo settings are visible
   */
  toggleSeoSettings() {
    this.setState(ps => ({ showSeoSettings: !ps.showSeoSettings }));
  }

  /**
   * toggles whether node info is visible
   */
  toggleNodeSettings() {
    this.setState(ps => ({ showNodeInfo: !ps.showNodeInfo }));
  }

  /**
   * Render publish option based on selection
   * @param {object} e - event
   */
  renderPublishInfo(e) {
    const { changePagePropertyDetail } = this.props;

    const isCompassPage = Boolean(e.target.id === 'compass');

    changePagePropertyDetail(isCompassPage, 'IsCompassPage');
    this.setState({ isCompassPage });
  }

  render() {
    const { isReadonly, node, pagePayload } = this.props;
    const { showNodeInfo, seoSettings, showSeoSettings, isCompassPage } = this.state;

    const { StartDate: startDate } = pagePayload;
    const isScheduled = !!startDate && new Date().toISOString() < startDate;
    const isLive = pagePayload.LiveNow;

    return (
      <div>
        <div className="p-4">
          {(isLive || isScheduled) && (
            <div className="mb-4" data-test-id="publish-options-live-date">
              <Label className="font-weight-bold mr-2">
                {isLive ? 'Live now as of:' : 'Live date scheduled for:'}
              </Label>
              {utcToPtDateString(startDate, 'L LT z')}
            </div>
          )}
          <div className={styles.publishOptions}>
            <div className={styles.inputWrapper}>
              <Input
                disabled={isReadonly}
                type="radio"
                name="publish"
                id="wcm"
                onChange={this.renderPublishInfo}
                checked={!isCompassPage}
                data-test-id="publish-options-use-wcm"
              />
              <Label for="wcm">Publish via WCM</Label>
            </div>
            <div className={styles.inputWrapper}>
              <Input
                disabled={isReadonly}
                type="radio"
                name="publish"
                id="compass"
                onChange={this.renderPublishInfo}
                checked={isCompassPage}
                data-test-id="publish-options-use-compass"
              />
              <Label for="compass">Publish via Compass</Label>
            </div>
          </div>
        </div>
        {!isCompassPage && (
          <div data-test-id="publish-options-routes" className="px-4 pb-4">
            {this.showRouteToggleButtons()}
            {this.setupRoutes(isLive || isScheduled)}
          </div>
        )}
        {isCompassPage ? (
          <div>
            <div data-test-id="publish-options-node-info">
              <SectionSubHeader onClick={this.toggleNodeSettings}>
                Node Information (from Compass)
                {renderArrowStyle(showNodeInfo)}
              </SectionSubHeader>
            </div>
            <Collapse isOpen={showNodeInfo}>
              <NodeInfo node={node} />
            </Collapse>
          </div>
        ) : (
          <div>
            <div data-test-id="publish-options-seo-settings">
              <SectionSubHeader onClick={this.toggleSeoSettings}>
                SEO Settings
                {renderArrowStyle(showSeoSettings)}
              </SectionSubHeader>
            </div>
            <Collapse isOpen={showSeoSettings}>
              <SeoSettings
                fields={seoSettings}
                handleFieldChange={this.handleSeoFieldChange}
                handleFieldUpdate={this.handleSeoFieldUpdate}
                handleCheckedUpdate={this.handleSeoCheckedChange}
                isReadonly={isReadonly}
              />
            </Collapse>
          </div>
        )}
      </div>
    );
  }
}
